import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Flex,
  Icon,
  List,
  ListItem,
  ListIcon,
  Badge,
  Skeleton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useBreakpointValue,
  useToast,
  Stack,
  Spinner,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import {
  FaStore,
  FaUsers,
  FaHeart,
  FaGift,
  FaCalendarAlt,
  FaBullhorn,
  FaHandshake,
  FaEnvelope,
  FaCheckCircle,
  FaGamepad,
  FaPaintBrush,
  FaRocket,
  FaChevronRight,
  FaHandHoldingHeart,
  FaLightbulb,
  FaFilePdf,
} from 'react-icons/fa';
import SostienicilHeroImage from '../images/sostienicil-hero.png';
import SponsorProjectInquiryForm from '../components/Sponsor/SponsorProjectInquiryForm';
import { generateSponsorSummaryPDF } from '../utils/generateSponsorSummaryPDF';
import {
  buildCommunityImpactFromStats,
  communityImpactForPdf,
} from '../utils/sponsorCommunityImpact';

const cilColors = {
  yellow: '#ffcc00',
  yellowDark: '#e6b800',
  teal: '#3eb297',
  sky: '#36a9e1',
  pink: '#e95995',
  plum: '#3b235a',
  dark: '#1a1a1a',
  darkMuted: '#2f3136',
};

/** Accenti alternati per le card progetto */
const PROJECT_CARD_ACCENTS = [
  cilColors.teal,
  cilColors.sky,
  cilColors.pink,
  cilColors.yellow,
  cilColors.plum,
];

const PAGE_PX = { base: 4, sm: 5, md: 8 };
const SECTION_PY = { base: 10, md: 14 };
const SECTION_PY_COMPACT = { base: 8, md: 12 };

const SPONSOR_MAIL_BASE =
  'mailto:info@cilassociazione.it?subject=Richiesta%20Sponsor%20CIL';

const track = (event, payload = {}) => {
  try {
    console.debug('[track]', event, payload);
  } catch {
    // ignore
  }
};

const benefitCards = [
  {
    icon: FaUsers,
    title: 'Visibilità',
    desc: 'Logo e menzione su eventi, social e materiali dedicati alla community CIL.',
    color: cilColors.yellow,
  },
  {
    icon: FaHeart,
    title: 'Impatto sociale',
    desc: 'Sostieni progetti educativi, ludici e culturali per bambini, ragazzi e famiglie.',
    color: cilColors.teal,
  },
  {
    icon: FaGift,
    title: 'Partnership',
    desc: 'Collaborazioni su misura: sconti ai soci, eventi in sede, co-branding e altro.',
    color: cilColors.sky,
  },
];

const collaborationTypes = [
  {
    icon: FaCalendarAlt,
    title: 'Eventi e attività',
    desc: 'Sponsorizza tornei, laboratori, serate e iniziative aperte alla comunità.',
  },
  {
    icon: FaStore,
    title: 'Sconti e vantaggi ai soci',
    desc: 'Offri promozioni riservate ai tesserati: compaiono nei piani CIL+ e nelle comunicazioni.',
  },
  {
    icon: FaBullhorn,
    title: 'Visibilità continuativa',
    desc: 'Presenza sul sito, newsletter e canali social durante campagne e progetti.',
  },
  {
    icon: FaHandshake,
    title: 'Progetti su misura',
    desc: 'Hai un’idea concreta? Raccontacela: costruiamo insieme una proposta ad hoc.',
  },
];

const whoWeLookFor = [
  'Attività commerciali e artigianali del territorio',
  'Ristoranti, bar e locali',
  'Negozi di gioco, cultura e tempo libero',
  'Studi professionali e servizi alla persona',
  'Aziende che vogliono investire nel sociale locale',
];

const SectionHeader = ({
  title,
  subtitle,
  badge,
  icon: HeaderIcon,
  light = false,
  accent = cilColors.plum,
}) => (
  <VStack
    spacing={3}
    textAlign="center"
    maxW="680px"
    mx="auto"
    px={{ base: 1, md: 0 }}
  >
    {badge}
    {HeaderIcon ? (
      <Flex
        align="center"
        justify="center"
        w={{ base: 12, md: 14 }}
        h={{ base: 12, md: 14 }}
        borderRadius="full"
        bg={light ? 'whiteAlpha.200' : `${accent}12`}
      >
        <Icon
          as={HeaderIcon}
          w={{ base: 5, md: 6 }}
          h={{ base: 5, md: 6 }}
          color={accent}
        />
      </Flex>
    ) : null}
    <Heading
      as="h2"
      size={{ base: 'md', sm: 'lg' }}
      fontWeight="extrabold"
      color={light ? 'white' : cilColors.dark}
      lineHeight="1.25"
    >
      {title}
    </Heading>
    {subtitle ? (
      <Text
        fontSize={{ base: 'sm', md: 'md' }}
        color={light ? 'gray.300' : 'gray.600'}
        lineHeight="1.7"
      >
        {subtitle}
      </Text>
    ) : null}
  </VStack>
);

const KpiCard = ({ kpi }) => (
  <Flex
    direction={{ base: 'row', sm: 'column' }}
    align={{ base: 'center', sm: 'center' }}
    textAlign={{ base: 'left', sm: 'center' }}
    gap={{ base: 4, sm: 0 }}
    bg="#25282d"
    borderRadius="xl"
    p={{ base: 4, md: 5 }}
    borderWidth="1px"
    borderColor="whiteAlpha.100"
    h="full"
    transition="border-color 0.2s ease, transform 0.2s ease"
    _hover={{ borderColor: kpi.color, transform: { md: 'translateY(-2px)' } }}
  >
    <Flex
      align="center"
      justify="center"
      flexShrink={0}
      w={{ base: 11, sm: 'auto' }}
      h={{ base: 11, sm: 'auto' }}
      borderRadius="full"
      bg={`${kpi.color}22`}
    >
      <Icon as={kpi.icon} w={5} h={5} color={kpi.color} />
    </Flex>
    <Box flex={1} minW={0}>
      <Text
        fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }}
        fontWeight="800"
        color="white"
        lineHeight="1.1"
      >
        {kpi.value}
      </Text>
      <Text
        fontSize="xs"
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="0.04em"
        color="gray.400"
        mt={1}
      >
        {kpi.label}
      </Text>
      <Text
        fontSize="xs"
        color="gray.500"
        lineHeight="1.5"
        mt={1}
        display={{ base: 'none', sm: 'block' }}
      >
        {kpi.detail}
      </Text>
    </Box>
  </Flex>
);

const Sponsor = () => {
  const toast = useToast();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [communityStats, setCommunityStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const {
    isOpen: isProjectModalOpen,
    onOpen: onProjectModalOpen,
    onClose: onProjectModalClose,
  } = useDisclosure();

  const modalSize = useBreakpointValue({ base: 'full', md: 'md' });

  const communityImpact = useMemo(
    () =>
      communityStats
        ? buildCommunityImpactFromStats(communityStats, cilColors)
        : null,
    [communityStats]
  );

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    onProjectModalOpen();
  };

  const closeProjectModal = () => {
    onProjectModalClose();
    setSelectedProject(null);
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchProjects = async () => {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        const response = await fetch('/api/projects/sponsor', {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Errore nel caricamento dei progetti');
        }
        setProjects(Array.isArray(data.projects) ? data.projects : []);
      } catch (err) {
        if (err.name === 'AbortError') return;
        setProjectsError(err.message || 'Errore di connessione');
        setProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjects();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCommunityStats = async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const response = await fetch('/api/statistics?period=12m', {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || data.error || 'Errore statistiche');
        }
        setCommunityStats(data);
      } catch (err) {
        if (err.name === 'AbortError') return;
        setStatsError(err.message || 'Errore di connessione');
        setCommunityStats(null);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchCommunityStats();
    return () => controller.abort();
  }, []);

  const openContact = () => {
    track('cta_sponsor_contattaci');
    window.open(SPONSOR_MAIL_BASE, '_blank');
  };

  const handleDownloadSummaryPdf = async () => {
    track('cta_sponsor_pdf');
    setIsGeneratingPdf(true);
    try {
      await generateSponsorSummaryPDF({
        projects,
        communityImpact: communityImpactForPdf(communityImpact),
        benefitCards: benefitCards.map(({ title, desc }) => ({ title, desc })),
        collaborationTypes: collaborationTypes.map(({ title, desc }) => ({
          title,
          desc,
        })),
        whoWeLookFor,
      });
      toast({
        title: 'PDF scaricato',
        description: 'Il riepilogo sponsor è stato generato.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Errore generazione PDF sponsor:', err);
      toast({
        title: 'Errore',
        description: 'Impossibile generare il PDF. Riprova.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <Box
      bg="gray.50"
      minH="100vh"
      color={cilColors.dark}
      pb={{ base: 6, md: 10 }}
    >
      {/* Hero */}
      <Box
        position="relative"
        minH={{ base: 'min(72vh, 420px)', sm: '380px', md: '400px' }}
        display="flex"
        alignItems="center"
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          backgroundImage={`url(${SostienicilHeroImage})`}
          backgroundPosition="center"
          backgroundSize="cover"
        />
        <Box
          position="absolute"
          inset={0}
          bgGradient="linear(to-b, rgba(59, 35, 90, 0.9) 0%, rgba(59, 35, 90, 0.78) 45%, rgba(26, 26, 26, 0.94) 100%)"
        />
        <Container
          maxW="container.lg"
          position="relative"
          zIndex={1}
          py={{ base: 10, md: 16 }}
          px={PAGE_PX}
        >
          <VStack
            spacing={{ base: 4, md: 5 }}
            textAlign="center"
            color="white"
            maxW="720px"
            mx="auto"
          >
            <Badge
              bg={cilColors.yellow}
              color={cilColors.plum}
              px={3}
              py={1}
              borderRadius="md"
              fontWeight="bold"
              fontSize="xs"
            >
              Partnership
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: '1.75rem', sm: '2.25rem', md: '3xl' }}
              fontWeight="800"
              letterSpacing="-0.03em"
              lineHeight="1.2"
              px={{ base: 1, sm: 0 }}
              textShadow="0 1px 3px rgba(0,0,0,0.5)"
            >
              Diventa sponsor di CIL
            </Heading>
            <Text
              fontSize={{ base: 'sm', md: 'lg' }}
              color="whiteAlpha.900"
              maxW="560px"
              lineHeight="1.65"
              px={{ base: 1, sm: 0 }}
            >
              Cerchiamo aziende e attività che vogliono crescere insieme a noi:
              visibilità nella community, impatto sociale reale e collaborazioni
              durature.
            </Text>
            <Stack
              direction={{ base: 'column', sm: 'row' }}
              spacing={3}
              w={{ base: 'full', sm: 'auto' }}
              align="stretch"
            >
              <Button
                size="lg"
                w={{ base: 'full', sm: 'auto' }}
                bg={cilColors.yellow}
                color={cilColors.plum}
                fontWeight="bold"
                borderRadius="xl"
                px={8}
                minH="48px"
                leftIcon={<FaEnvelope />}
                _hover={{ bg: cilColors.yellowDark }}
                onClick={openContact}
              >
                Contattaci
              </Button>
              <Button
                size="lg"
                w={{ base: 'full', sm: 'auto' }}
                variant="outline"
                borderColor="whiteAlpha.700"
                color="white"
                fontWeight="semibold"
                borderRadius="xl"
                px={6}
                minH="48px"
                leftIcon={<FaFilePdf />}
                _hover={{ bg: 'whiteAlpha.200', borderColor: 'white' }}
                onClick={handleDownloadSummaryPdf}
                isLoading={isGeneratingPdf}
                loadingText="PDF…"
              >
                Scarica riepilogo PDF
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>

      {/* Intro */}
      <Box py={SECTION_PY} bg="white">
        <Container maxW="container.md" px={PAGE_PX}>
          <SectionHeader
            icon={FaStore}
            accent={cilColors.yellow}
            title="Siamo sempre alla ricerca di sponsor"
            subtitle="CIL è un'associazione di promozione sociale: organizziamo eventi di gioco, laboratori creativi, attività per ragazzi e progetti educativi a Chieri e in provincia. Un partner sponsor ci aiuta a realizzare più iniziative e, in cambio, raggiunge una community attiva e coinvolta."
          />
        </Container>
      </Box>

      {/* Impatto community — dati reali ultimi 12 mesi */}
      <Box py={SECTION_PY} bg={cilColors.darkMuted} color="white">
        <Container maxW="container.xl" px={PAGE_PX}>
          <VStack spacing={{ base: 8, md: 10 }} align="stretch">
            <SectionHeader
              light
              badge={
                <Badge
                  bg="whiteAlpha.200"
                  color="white"
                  fontSize="xs"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  {communityImpact?.period || 'Ultimi 12 mesi'}
                </Badge>
              }
              title="L'impatto sui nostri soci"
              subtitle={
                communityImpact?.intro ||
                'Caricamento dati sulla community CIL…'
              }
            />

            {statsError ? (
              <Text textAlign="center" color="red.300" fontSize="sm">
                {statsError}
              </Text>
            ) : null}

            {statsLoading ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" color={cilColors.yellow} thickness="3px" />
              </Flex>
            ) : null}

            {!statsLoading && communityImpact ? (
              <>
                <SimpleGrid
                  columns={{ base: 1, sm: 2, lg: 3 }}
                  spacing={{ base: 3, md: 4 }}
                >
                  {communityImpact.kpis.map((kpi) => (
                    <KpiCard key={kpi.label} kpi={kpi} />
                  ))}
                </SimpleGrid>

                <Stack
                  direction={{ base: 'column', md: 'row' }}
                  spacing={{ base: 4, md: 6 }}
                  align="stretch"
                >
                  <Box
                    flex={1}
                    bg="#25282d"
                    borderRadius="xl"
                    p={{ base: 5, md: 6 }}
                    borderWidth="1px"
                    borderColor="whiteAlpha.100"
                  >
                    <Heading size="sm" mb={4} color={cilColors.yellow}>
                      Composizione dei soci
                    </Heading>
                    <VStack spacing={4} align="stretch">
                      {communityImpact.sociComposition.map((row) => (
                        <Box key={row.label}>
                          <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" color="gray.300">
                              {row.label}
                            </Text>
                            <Text fontSize="sm" fontWeight="bold">
                              {row.percent}%
                            </Text>
                          </HStack>
                          <Box
                            h="10px"
                            bg="whiteAlpha.100"
                            borderRadius="full"
                            overflow="hidden"
                          >
                            <Box
                              h="100%"
                              w={`${row.percent}%`}
                              bg={row.color}
                              borderRadius="full"
                            />
                          </Box>
                        </Box>
                      ))}
                    </VStack>
                  </Box>

                  <Box
                    flex={1}
                    bg="#25282d"
                    borderRadius="xl"
                    p={{ base: 5, md: 6 }}
                    borderWidth="1px"
                    borderColor="whiteAlpha.100"
                  >
                    <Heading size="sm" mb={4} color={cilColors.yellow}>
                      Attività per i soci
                    </Heading>
                    <VStack spacing={3} align="stretch">
                      {communityImpact.activityTypes.map((act) => (
                        <Flex
                          key={act.label}
                          justify="space-between"
                          align="flex-start"
                          gap={3}
                          py={2}
                          borderBottomWidth="1px"
                          borderColor="whiteAlpha.100"
                          _last={{ borderBottomWidth: 0 }}
                        >
                          <Text
                            fontSize="sm"
                            color="gray.300"
                            lineHeight="1.5"
                            flex={1}
                          >
                            {act.label}
                          </Text>
                          <Badge
                            bg={cilColors.plum}
                            color={cilColors.yellow}
                            flexShrink={0}
                            fontSize="xs"
                          >
                            {act.count}
                          </Badge>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                </Stack>

                <Box
                  textAlign="center"
                  py={{ base: 4, md: 5 }}
                  px={{ base: 4, md: 6 }}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor="rgba(255, 204, 0, 0.35)"
                  bg="rgba(255, 204, 0, 0.08)"
                >
                  <Text
                    fontSize={{ base: 'sm', md: 'md' }}
                    color="gray.200"
                    lineHeight="1.7"
                  >
                    {communityImpact.reach}
                  </Text>
                </Box>
              </>
            ) : null}
          </VStack>
        </Container>
      </Box>

      {/* Partnership benefits */}
      <Box py={SECTION_PY_COMPACT} bg="gray.50">
        <Container maxW="container.xl" px={PAGE_PX}>
          <VStack spacing={{ base: 6, md: 8 }} align="stretch">
            <SectionHeader
              title="Cosa offre una partnership"
              accent={cilColors.plum}
            />
            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing={{ base: 4, md: 5 }}
            >
              {benefitCards.map((item) => (
                <Box
                  key={item.title}
                  p={{ base: 5, md: 6 }}
                  bg="white"
                  borderRadius="xl"
                  textAlign="center"
                  borderWidth="1px"
                  borderColor="gray.100"
                  boxShadow="sm"
                  transition="border-color 0.2s ease, box-shadow 0.2s ease"
                  _hover={{ borderColor: item.color, boxShadow: 'md' }}
                >
                  <Flex
                    mx="auto"
                    mb={3}
                    w={12}
                    h={12}
                    align="center"
                    justify="center"
                    borderRadius="full"
                    bg={`${item.color}18`}
                  >
                    <Icon as={item.icon} w={6} h={6} color={item.color} />
                  </Flex>
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    color={cilColors.plum}
                    mb={2}
                  >
                    {item.title}
                  </Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                    {item.desc}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Chi cerchiamo + collaborazione */}
      <Box py={SECTION_PY} bg="white">
        <Container maxW="container.xl" px={PAGE_PX}>
          <Stack
            direction={{ base: 'column', lg: 'row' }}
            spacing={{ base: 8, lg: 10 }}
            align="stretch"
          >
            <Box
              flex={1}
              bg="gray.50"
              borderRadius="xl"
              p={{ base: 5, md: 6 }}
              borderWidth="1px"
              borderColor="gray.100"
            >
              <Heading size="md" mb={4} color={cilColors.dark}>
                Chi cerchiamo
              </Heading>
              <List spacing={3}>
                {whoWeLookFor.map((line) => (
                  <ListItem
                    key={line}
                    display="flex"
                    alignItems="flex-start"
                    fontSize={{ base: 'sm', md: 'md' }}
                    color="gray.700"
                    lineHeight="1.5"
                  >
                    <ListIcon
                      as={FaCheckCircle}
                      color={cilColors.teal}
                      mt={1}
                      flexShrink={0}
                    />
                    {line}
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box flex={1.2}>
              <Heading
                size="md"
                mb={4}
                color={cilColors.dark}
                px={{ base: 0, lg: 0 }}
              >
                Come possiamo collaborare
              </Heading>
              <VStack spacing={3} align="stretch">
                {collaborationTypes.map((item) => (
                  <HStack
                    key={item.title}
                    align="flex-start"
                    p={{ base: 4, md: 5 }}
                    bg="gray.50"
                    borderRadius="xl"
                    spacing={4}
                    borderWidth="1px"
                    borderColor="gray.100"
                    transition="border-color 0.2s ease"
                    _hover={{ borderColor: cilColors.yellow }}
                  >
                    <Flex
                      flexShrink={0}
                      w={10}
                      h={10}
                      align="center"
                      justify="center"
                      borderRadius="lg"
                      bg="white"
                    >
                      <Icon as={item.icon} w={5} h={5} color={cilColors.plum} />
                    </Flex>
                    <Box minW={0}>
                      <Text
                        fontWeight="bold"
                        color={cilColors.plum}
                        mb={1}
                        fontSize="sm"
                      >
                        {item.title}
                      </Text>
                      <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                        {item.desc}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Progetti da sostenere — sezione a colori */}
      <Box py={SECTION_PY} position="relative" overflow="hidden">
        <Box
          position="absolute"
          inset={0}
          bgGradient={`linear(145deg, ${cilColors.plum} 0%, #4a2d6e 28%, ${cilColors.teal} 55%, ${cilColors.sky} 78%, ${cilColors.yellow} 100%)`}
        />
        <Box
          position="absolute"
          top="-20%"
          right="-15%"
          w={{ base: '220px', md: '320px' }}
          h={{ base: '220px', md: '320px' }}
          borderRadius="full"
          bg={cilColors.yellow}
          opacity={0.25}
          filter="blur(60px)"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          bottom="-10%"
          left="-10%"
          w={{ base: '180px', md: '280px' }}
          h={{ base: '180px', md: '280px' }}
          borderRadius="full"
          bg={cilColors.pink}
          opacity={0.2}
          filter="blur(50px)"
          pointerEvents="none"
        />

        <Container
          maxW="container.lg"
          px={PAGE_PX}
          position="relative"
          zIndex={1}
        >
          <Box
            bg="white"
            borderRadius={{ base: '2xl', md: '3xl' }}
            p={{ base: 5, sm: 6, md: 8 }}
            boxShadow="2xl"
            borderWidth="1px"
            borderColor="whiteAlpha.800"
          >
            <VStack spacing={{ base: 6, md: 8 }} align="stretch">
              <VStack spacing={4} textAlign="center">
                <Flex
                  align="center"
                  justify="center"
                  w={{ base: 14, md: 16 }}
                  h={{ base: 14, md: 16 }}
                  borderRadius="2xl"
                  bgGradient={`linear(135deg, ${cilColors.plum}, ${cilColors.teal})`}
                  boxShadow={`0 8px 24px ${cilColors.plum}44`}
                >
                  <Icon
                    as={FaRocket}
                    w={{ base: 6, md: 7 }}
                    h={{ base: 6, md: 7 }}
                    color="white"
                  />
                </Flex>

                <Heading
                  as="h2"
                  size={{ base: 'lg', md: 'xl' }}
                  fontWeight="extrabold"
                  bgGradient={`linear(to-r, ${cilColors.plum}, ${cilColors.teal}, ${cilColors.sky})`}
                  bgClip="text"
                  lineHeight="1.2"
                  px={{ base: 1, md: 0 }}
                >
                  Progetti da sostenere
                </Heading>

                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  color="gray.600"
                  maxW="600px"
                  lineHeight="1.75"
                  px={{ base: 1, md: 0 }}
                >
                  Ecco le iniziative attualmente in lavorazione in CIL: ogni
                  progetto può beneficiare di{' '}
                  <Box as="span" color={cilColors.teal} fontWeight="semibold">
                    sponsor e partner
                  </Box>{' '}
                  che vogliono contribuire alla realizzazione.
                </Text>

                <Button
                  size="md"
                  w={{ base: 'full', sm: 'auto' }}
                  variant="outline"
                  borderColor={cilColors.plum}
                  color={cilColors.plum}
                  borderRadius="xl"
                  leftIcon={<FaFilePdf />}
                  _hover={{
                    bg: `${cilColors.yellow}33`,
                    borderColor: cilColors.yellow,
                  }}
                  onClick={handleDownloadSummaryPdf}
                  isLoading={isGeneratingPdf}
                  loadingText="Generazione PDF…"
                >
                  Scarica brochure PDF riassuntiva
                </Button>
              </VStack>

              {projectsLoading ? (
                <VStack spacing={3} align="stretch">
                  {[
                    cilColors.teal,
                    cilColors.sky,
                    cilColors.pink,
                    cilColors.yellow,
                  ].map((color) => (
                    <Skeleton
                      key={color}
                      height="72px"
                      borderRadius="xl"
                      startColor={`${color}22`}
                      endColor={`${color}44`}
                    />
                  ))}
                </VStack>
              ) : projectsError ? (
                <Box
                  textAlign="center"
                  py={6}
                  px={4}
                  borderRadius="xl"
                  bg="red.50"
                  borderWidth="1px"
                  borderColor="red.100"
                >
                  <Text color="red.600" fontSize="sm" fontWeight="medium">
                    {projectsError}
                  </Text>
                </Box>
              ) : projects.length === 0 ? (
                <Flex
                  direction="column"
                  align="center"
                  textAlign="center"
                  py={8}
                  px={4}
                  borderRadius="xl"
                  bg={`linear-gradient(135deg, ${cilColors.yellow}18, ${cilColors.teal}12)`}
                  borderWidth="1px"
                  borderColor={`${cilColors.yellow}44`}
                >
                  <Icon
                    as={FaLightbulb}
                    w={8}
                    h={8}
                    color={cilColors.plum}
                    mb={3}
                  />
                  <Text
                    color="gray.600"
                    lineHeight="1.7"
                    fontSize="sm"
                    maxW="400px"
                  >
                    Al momento non ci sono progetti attivi da mostrare. Torna a
                    trovarci presto oppure scrivici per proporre una
                    collaborazione.
                  </Text>
                  <Button
                    mt={4}
                    size="sm"
                    bg={cilColors.plum}
                    color="white"
                    _hover={{ bg: '#2d1838' }}
                    onClick={openContact}
                  >
                    Contattaci
                  </Button>
                </Flex>
              ) : (
                <VStack
                  as="ul"
                  spacing={{ base: 3, md: 4 }}
                  align="stretch"
                  listStyleType="none"
                  m={0}
                  p={0}
                >
                  {projects.map((project, index) => {
                    const accent =
                      PROJECT_CARD_ACCENTS[index % PROJECT_CARD_ACCENTS.length];
                    return (
                      <Box as="li" key={project.documentId || project.id}>
                        <Box
                          as="button"
                          type="button"
                          w="full"
                          textAlign="left"
                          minH={{ base: '64px', md: '72px' }}
                          py={{ base: 4, md: 5 }}
                          pl={{ base: 4, md: 5 }}
                          pr={{ base: 4, md: 5 }}
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor={`${accent}33`}
                          bgGradient={`linear(105deg, white 0%, ${accent}0c 55%, ${accent}18 100%)`}
                          boxShadow={`0 4px 14px ${accent}22`}
                          cursor="pointer"
                          position="relative"
                          overflow="hidden"
                          transition="all 0.22s ease"
                          _before={{
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            w: '5px',
                            bg: accent,
                            borderRadius: 'xl 0 0 xl',
                          }}
                          _hover={{
                            borderColor: accent,
                            boxShadow: `0 8px 28px ${accent}40`,
                            transform: { md: 'translateY(-2px)' },
                          }}
                          _active={{ transform: 'scale(0.99)' }}
                          _focus={{
                            outline: '2px solid',
                            outlineColor: accent,
                            outlineOffset: '2px',
                          }}
                          onClick={() => handleProjectClick(project)}
                        >
                          <HStack spacing={4} align="center" pl={2}>
                            <Flex
                              flexShrink={0}
                              w={{ base: 10, md: 11 }}
                              h={{ base: 10, md: 11 }}
                              align="center"
                              justify="center"
                              borderRadius="lg"
                              bg={accent}
                              color="white"
                              boxShadow={`0 4px 12px ${accent}55`}
                            >
                              <Icon
                                as={FaHandHoldingHeart}
                                boxSize={{ base: 4, md: 5 }}
                              />
                            </Flex>
                            <Box flex={1} minW={0}>
                              <Text
                                fontSize={{ base: 'sm', md: 'md' }}
                                fontWeight="bold"
                                color={cilColors.plum}
                                lineHeight="1.35"
                                noOfLines={2}
                              >
                                {project.title}
                              </Text>
                              <Text
                                fontSize="xs"
                                color={accent}
                                fontWeight="semibold"
                                mt={0.5}
                              >
                                Scopri il progetto e invia una richiesta
                              </Text>
                            </Box>
                            <Flex
                              flexShrink={0}
                              w={8}
                              h={8}
                              align="center"
                              justify="center"
                              borderRadius="full"
                              bg={`${accent}22`}
                            >
                              <Icon
                                as={FaChevronRight}
                                color={accent}
                                boxSize={3.5}
                              />
                            </Flex>
                          </HStack>
                        </Box>
                      </Box>
                    );
                  })}
                </VStack>
              )}

              {!projectsLoading && !projectsError && projects.length > 0 ? (
                <Text
                  fontSize="xs"
                  color="gray.500"
                  textAlign="center"
                  pt={2}
                  lineHeight="1.5"
                >
                  Ogni progetto è un&apos;opportunità concreta di partnership:
                  apri la scheda per leggere i dettagli e proporti come sponsor.
                </Text>
              ) : null}
            </VStack>
          </Box>
        </Container>
      </Box>

      <Modal
        isOpen={isProjectModalOpen}
        onClose={closeProjectModal}
        size={modalSize}
        scrollBehavior="inside"
        motionPreset={modalSize === 'full' ? 'slideInBottom' : 'scale'}
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          borderRadius={{ base: 0, md: 'xl' }}
          mx={{ base: 0, md: 4 }}
          my={{ base: 0, md: 'auto' }}
          maxH={{ base: '100dvh', md: '90vh' }}
          mt={{ base: 0, md: undefined }}
        >
          <ModalHeader
            color={cilColors.plum}
            pr={12}
            fontSize={{ base: 'md', md: 'lg' }}
            lineHeight="1.35"
            pt={{ base: 5, md: 6 }}
            px={{ base: 4, md: 6 }}
          >
            {selectedProject?.title}
          </ModalHeader>
          <ModalCloseButton top={{ base: 3, md: 4 }} />
          <ModalBody pb={{ base: 6, md: 8 }} px={{ base: 4, md: 6 }}>
            <Text fontSize="sm" color="gray.600" lineHeight="1.7">
              {selectedProject?.descrizione?.trim()
                ? selectedProject.descrizione
                : 'Descrizione non ancora disponibile per questo progetto.'}
            </Text>
            {selectedProject ? (
              <SponsorProjectInquiryForm
                key={selectedProject.documentId || selectedProject.id}
                project={selectedProject}
              />
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Perché CIL */}
      <Box py={SECTION_PY_COMPACT} bg={cilColors.darkMuted} color="white">
        <Container maxW="container.md" px={PAGE_PX}>
          <VStack spacing={{ base: 6, md: 8 }}>
            <SectionHeader light title="Perché CIL" />
            <SimpleGrid
              columns={{ base: 1, sm: 3 }}
              spacing={{ base: 4, md: 6 }}
              w="full"
            >
              {[
                {
                  icon: FaGamepad,
                  label: 'Gioco e cultura',
                  sub: communityStats
                    ? `${(communityStats.totalEvents || 0).toLocaleString('it-IT')} eventi negli ultimi 12 mesi`
                    : 'Eventi, tornei e laboratori per tutte le età',
                },
                {
                  icon: FaPaintBrush,
                  label: 'Progetti educativi',
                  sub: communityStats
                    ? `${(communityStats.workshopsCount || 0).toLocaleString('it-IT')} laboratori e attività formative`
                    : 'Attività per bambini, ragazzi e famiglie',
                },
                {
                  icon: FaUsers,
                  label: 'Community attiva',
                  sub: communityStats
                    ? `${(communityStats.totalMembers || 0).toLocaleString('it-IT')} soci tesserati · ${(communityStats.totalParticipations || 0).toLocaleString('it-IT')} partecipazioni`
                    : 'Soci e partecipazioni aggiornate dagli ultimi 12 mesi',
                },
              ].map((item) => (
                <Box
                  key={item.label}
                  bg="#25282d"
                  borderRadius="xl"
                  p={{ base: 5, md: 4 }}
                  borderWidth="1px"
                  borderColor="whiteAlpha.100"
                  textAlign={{ base: 'left', sm: 'center' }}
                >
                  <HStack
                    spacing={4}
                    justify={{ base: 'flex-start', sm: 'center' }}
                    align="flex-start"
                    direction={{ base: 'row', sm: 'column' }}
                  >
                    <Icon
                      as={item.icon}
                      w={6}
                      h={6}
                      color={cilColors.yellow}
                      flexShrink={0}
                    />
                    <VStack
                      spacing={1}
                      align={{ base: 'flex-start', sm: 'center' }}
                      flex={1}
                    >
                      <Text fontWeight="bold" fontSize="sm">
                        {item.label}
                      </Text>
                      <Text fontSize="sm" color="gray.300" lineHeight="1.6">
                        {item.sub}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </SimpleGrid>
            <Text
              fontSize="sm"
              color="gray.300"
              maxW="560px"
              lineHeight="1.7"
              textAlign="center"
            >
              I partner che offrono sconti o vantaggi ai soci possono essere
              inclusi nei piani{' '}
              <Box
                as={Link}
                to="/cilplus"
                color={cilColors.yellow}
                fontWeight="semibold"
              >
                CIL+
              </Box>{' '}
              e nelle comunicazioni dell&apos;associazione.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* CTA finale */}
      <Box py={SECTION_PY} bg="white">
        <Container maxW="container.sm" px={PAGE_PX}>
          <Box
            bg={cilColors.dark}
            borderRadius={{ base: 'xl', md: '2xl' }}
            p={{ base: 6, sm: 8, md: 10 }}
            textAlign="center"
            color="white"
            boxShadow="lg"
          >
            <Heading size={{ base: 'md', md: 'lg' }} mb={3} color="white">
              Vuoi collaborare con noi?
            </Heading>
            <Text
              fontSize={{ base: 'sm', md: 'md' }}
              color="gray.300"
              mb={6}
              lineHeight="1.7"
            >
              Scrivici con una breve presentazione della tua attività e
              l&apos;idea di collaborazione. Ti risponderemo per definire
              insieme i prossimi passi.
            </Text>
            <VStack spacing={3} w="full">
              <Button
                size="lg"
                w="full"
                minH="48px"
                bg={cilColors.yellow}
                color={cilColors.plum}
                leftIcon={<FaStore />}
                fontWeight="bold"
                borderRadius="xl"
                _hover={{ bg: cilColors.yellowDark }}
                onClick={openContact}
              >
                Richiedi informazioni sponsor
              </Button>
              <Button
                as={Link}
                to="/contattaci"
                variant="ghost"
                color="gray.300"
                size="md"
                w="full"
                minH="44px"
                _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                onClick={() => track('cta_sponsor_contattaci_form')}
              >
                Oppure usa il modulo Contattaci
              </Button>
            </VStack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Sponsor;
